import { useAgent, useAgentToolEvents } from "agents/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { api } from "./api";
import { FirmToolsMenu } from "./FirmTools";
import { MiniAppCard } from "./MiniAppCard";
import { MatterFilesDialog } from "./MatterFilesDialog";
import { PwaControls } from "./PwaControls";
import paraPlusLogo from "../../../website/assets/paraplus-logo.png";
import type {
  AgentDefinition,
  Approval,
  BillingType,
  Channel,
  ChannelSummary,
  Client,
  ClientType,
  Document,
  Lawyer,
  Matter,
  MatterType,
  Message,
  RealtimeEvent,
  WorkflowRun,
  WorkflowType,
  SearchResult,
  AgentSkill,
  AgentSkillCategory,
  KnowledgeResult,
  Invoice,
  InvoiceReview,
  TimeEntry,
  TimeEntryType,
  MentionableUser,
} from "./types";

const MATTER_TYPES: Array<{ value: MatterType; label: string }> = [
  { value: "Litigation", label: "Litigation" },
  { value: "Transactional", label: "Transactional" },
  { value: "Advisory", label: "Advisory" },
  { value: "RealEstate", label: "Real estate" },
  { value: "Corporate", label: "Corporate" },
  { value: "FamilyLaw", label: "Family law" },
  { value: "CriminalDefense", label: "Criminal defense" },
  { value: "Immigration", label: "Immigration" },
  { value: "IntellectualProperty", label: "Intellectual property" },
  { value: "Employment", label: "Employment" },
  { value: "Bankruptcy", label: "Bankruptcy" },
  { value: "Estate", label: "Estate" },
  { value: "Other", label: "Other" },
];
const CLIENT_TYPES: ClientType[] = [
  "Individual", "Corporation", "Partnership", "LLC", "Trust", "Estate", "Government", "Other",
];
const BILLING_TYPES: Array<{ value: BillingType; label: string }> = [
  { value: "Hourly", label: "Hourly" },
  { value: "FlatFee", label: "Flat fee" },
  { value: "Contingency", label: "Contingency" },
  { value: "Retainer", label: "Retainer" },
  { value: "ProBono", label: "Pro bono" },
  { value: "Hybrid", label: "Hybrid" },
];
const TIME_ENTRY_TYPES: Array<{ value: TimeEntryType; label: string }> = [
  { value: "Research", label: "Research" },
  { value: "Drafting", label: "Drafting" },
  { value: "CourtAppearance", label: "Court appearance" },
  { value: "ClientMeeting", label: "Client meeting" },
  { value: "PhoneCall", label: "Phone call" },
  { value: "Email", label: "Email" },
  { value: "Travel", label: "Travel" },
  { value: "Review", label: "Review" },
  { value: "Conference", label: "Conference" },
  { value: "Negotiation", label: "Negotiation" },
  { value: "Other", label: "Other" },
];

const AGENT_MENTION_NAMES: Record<string, string> = {
  paraplus: "ParaPlus",
  "legal-research": "Research",
  drafting: "Drafting",
  "docketing-specialist": "Docketing",
  "trust-accounting": "Trust",
  "firm-admin": "Accounting",
};

function agentMentionName(agent: AgentDefinition): string {
  return AGENT_MENTION_NAMES[agent.slug] ?? agent.name;
}

function initials(value: string): string {
  return value.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(new Date(value));
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(value);
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));
}

function upsertMessage(items: Message[], next: Message): Message[] {
  const index = items.findIndex((item) => item.id === next.id);
  if (index < 0) return [...items, next].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const copy = [...items];
  copy[index] = next;
  return copy;
}

function upsertWorkflow(items: WorkflowRun[], next: WorkflowRun): WorkflowRun[] {
  const index = items.findIndex((item) => item.id === next.id);
  const statusRank: Record<WorkflowRun["status"], number> = {
    queued: 0,
    running: 1,
    awaiting_approval: 2,
    completed: 3,
    failed: 3,
    cancelled: 3,
  };
  if (index >= 0 && statusRank[next.status] < statusRank[items[index].status]) return items;
  const updated = index < 0
    ? [...items, next]
    : items.map((item) => item.id === next.id ? next : item);
  return updated.sort((left, right) => right.createdAt.localeCompare(left.createdAt));
}

function Avatar({ label, agent = false }: { label: string; agent?: boolean }) {
  return <span className={`avatar${agent ? " agent-avatar" : ""}`}>{label}</span>;
}

function FirmToolsIcon() {
  return (
    <svg className="firm-tools-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 6V4.75C9 3.78 9.78 3 10.75 3h2.5C14.22 3 15 3.78 15 4.75V6" />
      <path d="M5 6h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z" />
      <path d="M3 11.5c5.5 2.5 12.5 2.5 18 0M10 12h4v3h-4z" />
    </svg>
  );
}

function SkillsIcon() {
  return (
    <svg className="rail-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="m12 3 1.2 3.8L17 8l-3.8 1.2L12 13l-1.2-3.8L7 8l3.8-1.2L12 3Z" />
      <path d="m18.5 13 .7 2.3 2.3.7-2.3.7-.7 2.3-.7-2.3-2.3-.7 2.3-.7.7-2.3ZM5 12l.8 2.2L8 15l-2.2.8L5 18l-.8-2.2L2 15l2.2-.8L5 12Z" />
    </svg>
  );
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function MessageBody({ message }: { message: Message }) {
  if (message.mentions.length === 0) return <div className="message-body">{message.body}</div>;
  const mentionNames = message.mentions.map((mention) => mention.displayName);
  const pattern = new RegExp(`(@(?:${mentionNames.map(escapeRegExp).join("|")}))`, "gi");
  const normalized = new Set(mentionNames.map((name) => `@${name}`.toLocaleLowerCase()));
  return (
    <div className="message-body">
      {message.body.split(pattern).map((part, index) => normalized.has(part.toLocaleLowerCase())
        ? <span className="human-mention" key={`${part}-${index}`}>{part}</span>
        : part)}
    </div>
  );
}

function Modal({
  title,
  description,
  onClose,
  children,
  className,
}: {
  title: string;
  description: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}) {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className={className ? `modal ${className}` : "modal"} role="dialog" aria-modal="true" aria-labelledby="modal-title" aria-describedby="modal-description">
        <header>
          <div><h2 id="modal-title">{title}</h2><p id="modal-description">{description}</p></div>
          <button className="modal-close" type="button" onClick={onClose} aria-label="Close dialog">×</button>
        </header>
        {children}
      </section>
    </div>
  );
}

function NewMatterDialog({
  clients,
  onClose,
  onCreated,
}: {
  clients: Client[];
  onClose: () => void;
  onCreated: (result: { client: Client; matter: Matter; channel: Channel }) => void;
}) {
  const [clientChoice, setClientChoice] = useState(clients[0]?.id ?? "new");
  const [clientName, setClientName] = useState("");
  const [clientType, setClientType] = useState<ClientType>("Individual");
  const [clientEmail, setClientEmail] = useState("");
  const [matterName, setMatterName] = useState("");
  const [matterType, setMatterType] = useState<MatterType>("Litigation");
  const [billingType, setBillingType] = useState<BillingType>("Hourly");
  const [jurisdiction, setJurisdiction] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const result = await api.createMatter({
        client: clientChoice === "new"
          ? { kind: "new", name: clientName, type: clientType, email: clientEmail || null }
          : { kind: "existing", clientId: clientChoice },
        name: matterName,
        type: matterType,
        billingType,
        jurisdiction: jurisdiction || null,
        description: description || null,
      });
      onCreated(result);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Matter could not be created.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Open a new matter" description="Create the client and matter workspace together." onClose={onClose}>
      <form className="modal-form" onSubmit={(event) => void submit(event)}>
        <label className="field field-wide">
          <span>Client</span>
          <select value={clientChoice} onChange={(event) => setClientChoice(event.target.value)}>
            {clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
            <option value="new">＋ Add a new client</option>
          </select>
        </label>

        {clientChoice === "new" && (
          <div className="form-grid inset-fields">
            <label className="field field-wide">
              <span>Client name</span>
              <input autoFocus required maxLength={300} value={clientName} onChange={(event) => setClientName(event.target.value)} placeholder="Person or organization" />
            </label>
            <label className="field">
              <span>Client type</span>
              <select value={clientType} onChange={(event) => setClientType(event.target.value as ClientType)}>
                {CLIENT_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Email <small>optional</small></span>
              <input type="email" maxLength={320} value={clientEmail} onChange={(event) => setClientEmail(event.target.value)} placeholder="client@example.com" />
            </label>
          </div>
        )}

        <div className="form-divider" />
        <div className="form-grid">
          <label className="field field-wide">
            <span>Matter name</span>
            <input autoFocus={clientChoice !== "new"} required maxLength={300} value={matterName} onChange={(event) => setMatterName(event.target.value)} placeholder="Smith v. Jones" />
          </label>
          <label className="field">
            <span>Matter type</span>
            <select value={matterType} onChange={(event) => setMatterType(event.target.value as MatterType)}>
              {MATTER_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Billing</span>
            <select value={billingType} onChange={(event) => setBillingType(event.target.value as BillingType)}>
              {BILLING_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
            </select>
          </label>
          <label className="field field-wide">
            <span>Jurisdiction <small>optional</small></span>
            <input maxLength={500} value={jurisdiction} onChange={(event) => setJurisdiction(event.target.value)} placeholder="Ohio, S.D.N.Y., etc." />
          </label>
          <label className="field field-wide">
            <span>Description <small>optional</small></span>
            <textarea maxLength={5000} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Brief background or objective" />
          </label>
        </div>
        {error && <p className="form-error">{error}</p>}
        <footer className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button type="submit" className="primary-button" disabled={saving}>{saving ? "Opening matter…" : "Open matter"}</button>
        </footer>
      </form>
    </Modal>
  );
}

function NewChannelDialog({
  matter,
  onClose,
  onCreated,
}: {
  matter: Matter;
  onClose: () => void;
  onCreated: (channel: Channel) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const result = await api.createChannel(matter.id, { name, description, isPrivate });
      onCreated(result.channel);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Channel could not be created.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Create a channel" description={`Add a focused conversation to ${matter.matterNumber}.`} onClose={onClose}>
      <form className="modal-form" onSubmit={(event) => void submit(event)}>
        <label className="field">
          <span>Channel name</span>
          <input autoFocus required maxLength={100} value={name} onChange={(event) => setName(event.target.value)} placeholder="Case strategy" />
        </label>
        <label className="field">
          <span>Description <small>optional</small></span>
          <textarea maxLength={5000} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="What belongs in this channel?" />
        </label>
        <label className="checkbox-field">
          <input type="checkbox" checked={isPrivate} onChange={(event) => setIsPrivate(event.target.checked)} />
          <span><strong>Private channel</strong><small>Only members added to this channel can see it.</small></span>
        </label>
        {error && <p className="form-error">{error}</p>}
        <footer className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button type="submit" className="primary-button" disabled={saving}>{saving ? "Creating…" : "Create channel"}</button>
        </footer>
      </form>
    </Modal>
  );
}

function DeleteChannelDialog({
  channel,
  onClose,
  onDeleted,
}: {
  channel: Channel;
  onClose: () => void;
  onDeleted: (channelId: string) => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    setDeleting(true);
    setError(null);
    try {
      await api.deleteChannel(channel.id);
      onDeleted(channel.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Channel could not be deleted.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      title={`Delete #${channel.name}?`}
      description="This removes the channel from the matter workspace for everyone."
      onClose={onClose}
    >
      <div className="modal-form delete-channel-confirmation">
        <p>Messages and activity will remain preserved in the firm’s audit history, but the channel will no longer be available for conversation.</p>
        {error && <p className="form-error">{error}</p>}
        <footer className="modal-actions">
          <button type="button" className="secondary-button" disabled={deleting} onClick={onClose}>Keep channel</button>
          <button type="button" className="danger-button" disabled={deleting} onClick={() => void remove()}>
            {deleting ? "Deleting…" : "Delete channel"}
          </button>
        </footer>
      </div>
    </Modal>
  );
}

function InvoiceReviewDialog({
  review,
  onClose,
  onUpdated,
}: {
  review: InvoiceReview;
  onClose: () => void;
  onUpdated: (review: InvoiceReview) => void;
}) {
  type EditableInvoiceLine = {
    id: string | null;
    clientKey: string;
    description: string;
    quantity: number;
    rate: number;
    costAdvanceId: string | null;
  };
  const [markingSent, setMarkingSent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const invoice = review.invoice;
  const [invoiceDate, setInvoiceDate] = useState(invoice.invoiceDate.slice(0, 10));
  const [dueDate, setDueDate] = useState(invoice.dueDate.slice(0, 10));
  const [notes, setNotes] = useState(invoice.notes ?? "");
  const [draftLines, setDraftLines] = useState<EditableInvoiceLine[]>(() => review.lineItems.map((line) => ({
    id: line.id,
    clientKey: line.id,
    description: line.description,
    quantity: line.quantity,
    rate: line.rate,
    costAdvanceId: line.costAdvanceId,
  })));
  const draftTotal = draftLines.reduce((sum, line) => sum + line.quantity * line.rate, 0);

  const resetDraft = () => {
    setInvoiceDate(invoice.invoiceDate.slice(0, 10));
    setDueDate(invoice.dueDate.slice(0, 10));
    setNotes(invoice.notes ?? "");
    setDraftLines(review.lineItems.map((line) => ({
      id: line.id,
      clientKey: line.id,
      description: line.description,
      quantity: line.quantity,
      rate: line.rate,
      costAdvanceId: line.costAdvanceId,
    })));
  };

  const saveDraft = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await api.updateInvoiceDraft(invoice.id, {
        expectedVersion: invoice.concurrencyVersion,
        invoiceDate,
        dueDate,
        notes: notes.trim() || null,
        lineItems: draftLines.map(({ id, description, quantity, rate }) => ({
          id,
          description,
          quantity,
          rate,
        })),
      });
      onUpdated(updated);
      setEditing(false);
      setNotice("Draft changes saved.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The invoice changes could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const markSent = async () => {
    setMarkingSent(true);
    setError(null);
    try {
      const result = await api.markInvoiceSent(invoice.id);
      onUpdated({ ...review, invoice: result.invoice });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The invoice could not be marked sent.");
    } finally {
      setMarkingSent(false);
    }
  };

  return (
    <Modal
      title={invoice.invoiceNumber}
      description={`${invoice.status} invoice · issued ${formatDate(invoice.invoiceDate)} · due ${formatDate(invoice.dueDate)}`}
      onClose={onClose}
    >
      <div className="invoice-review">
        <div className="invoice-totals">
          <div><span>Total</span><strong>{formatMoney(editing ? draftTotal : invoice.total)}</strong></div>
          <div><span>Paid</span><strong>{formatMoney(invoice.amountPaid)}</strong></div>
          <div><span>Balance</span><strong>{formatMoney(editing ? draftTotal : invoice.balance)}</strong></div>
        </div>
        {editing ? (
          <div className="invoice-edit-form">
            <div className="form-grid">
              <label className="field"><span>Invoice date</span><input type="date" value={invoiceDate} onChange={(event) => setInvoiceDate(event.target.value)} /></label>
              <label className="field"><span>Due date</span><input type="date" min={invoiceDate} value={dueDate} onChange={(event) => setDueDate(event.target.value)} /></label>
              <label className="field field-wide"><span>Notes <small>optional</small></span><textarea value={notes} maxLength={10_000} onChange={(event) => setNotes(event.target.value)} /></label>
            </div>
            <section>
              <div className="invoice-edit-heading"><h3>Invoice items</h3><button type="button" onClick={() => setDraftLines((current) => [...current, { id: null, clientKey: crypto.randomUUID(), description: "", quantity: 1, rate: 0, costAdvanceId: null }])}>＋ Add line</button></div>
              <div className="invoice-line-editors">
                {draftLines.map((item, index) => (
                  <div key={item.clientKey}>
                    <label className="field invoice-line-description"><span>Description</span><textarea required maxLength={5_000} value={item.description} onChange={(event) => setDraftLines((current) => current.map((line, lineIndex) => lineIndex === index ? { ...line, description: event.target.value } : line))} /></label>
                    <label className="field"><span>Quantity</span><input required disabled={item.costAdvanceId !== null} type="number" min="0.01" step="0.01" value={item.quantity} onChange={(event) => setDraftLines((current) => current.map((line, lineIndex) => lineIndex === index ? { ...line, quantity: Number(event.target.value) } : line))} /></label>
                    <label className="field"><span>Rate</span><input required disabled={item.costAdvanceId !== null} type="number" min="0" step="0.01" value={item.rate} onChange={(event) => setDraftLines((current) => current.map((line, lineIndex) => lineIndex === index ? { ...line, rate: Number(event.target.value) } : line))} /></label>
                    <span className="invoice-line-controls"><strong>{formatMoney(item.quantity * item.rate)}</strong><button type="button" aria-label={`Remove ${item.description || "invoice line"}`} disabled={draftLines.length === 1} onClick={() => setDraftLines((current) => current.filter((_, lineIndex) => lineIndex !== index))}>Remove</button></span>
                    {item.costAdvanceId && <small className="invoice-line-lock">Linked hard-cost amount is locked. Remove it to return the cost to unbilled.</small>}
                  </div>
                ))}
              </div>
            </section>
          </div>
        ) : (
          <>
            <section>
              <h3>Invoice items</h3>
              <div className="invoice-lines">
                {review.lineItems.map((item) => (
                  <div key={item.id}>
                    <span><strong>{item.description}</strong><small>{item.quantity} × {formatMoney(item.rate)}</small></span>
                    <b>{formatMoney(item.amount)}</b>
                  </div>
                ))}
              </div>
            </section>
            {invoice.notes && <section><h3>Notes</h3><p>{invoice.notes}</p></section>}
          </>
        )}
        {review.payments.length > 0 && (
          <section>
            <h3>Payments</h3>
            <div className="invoice-lines">
              {review.payments.map((payment) => (
                <div key={payment.id}>
                  <span><strong>{payment.method}</strong><small>{formatDate(payment.paymentDate)}{payment.referenceNumber ? ` · ${payment.referenceNumber}` : ""}</small></span>
                  <b>{formatMoney(payment.amount)}</b>
                </div>
              ))}
            </div>
          </section>
        )}
        {invoice.status === "Draft" && !editing && (
          <div className="invoice-posting-notice">
            <strong>What approval does</strong>
            <p>This marks the invoice Sent and posts {formatMoney(invoice.total)} to Accounts Receivable and the applicable revenue accounts. It does not email or otherwise deliver the invoice to the client.</p>
          </div>
        )}
        {invoice.status !== "Draft" && invoice.sentDate && (
          <p className="invoice-sent-note">Marked sent on {formatDate(invoice.sentDate)}.</p>
        )}
        {notice && <p className="invoice-sent-note">{notice}</p>}
        {error && <p className="form-error">{error}</p>}
        <footer className="modal-actions">
          {editing ? (
            <>
              <button type="button" className="secondary-button" disabled={saving} onClick={() => { resetDraft(); setEditing(false); setError(null); }}>Cancel</button>
              <button type="button" className="primary-button" disabled={saving} onClick={() => void saveDraft()}>{saving ? "Saving…" : "Save changes"}</button>
            </>
          ) : (
            <>
              <button type="button" className="secondary-button" onClick={onClose}>Close</button>
              {invoice.status === "Draft" && <button type="button" className="secondary-button" onClick={() => { resetDraft(); setEditing(true); setNotice(null); }}>Edit invoice</button>}
              {invoice.status === "Draft" && (
                <button type="button" className="primary-button" disabled={markingSent} onClick={() => void markSent()}>
                  {markingSent ? "Posting invoice…" : "Approve and mark sent"}
                </button>
              )}
            </>
          )}
        </footer>
      </div>
    </Modal>
  );
}

function TimeEntryDialog({
  entry,
  onClose,
  onUpdated,
  onDeleted,
}: {
  entry: TimeEntry;
  onClose: () => void;
  onUpdated: (entry: TimeEntry) => void;
  onDeleted: (entryId: string) => void;
}) {
  const [date, setDate] = useState(entry.date.slice(0, 10));
  const [hours, setHours] = useState(entry.hours);
  const [description, setDescription] = useState(entry.description);
  const [type, setType] = useState<TimeEntryType>(entry.type);
  const [isBillable, setIsBillable] = useState(entry.isBillable);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const amount = isBillable ? hours * (entry.billableRate ?? 0) : 0;

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const result = await api.updateTimeEntry(entry.id, {
        expectedVersion: entry.concurrencyVersion,
        date,
        hours,
        description: description.trim(),
        type,
        isBillable,
      });
      onUpdated(result.timeEntry);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The time entry could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await api.deleteTimeEntry(entry.id);
      onDeleted(entry.id);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The time entry could not be deleted.");
      setDeleting(false);
    }
  };

  if (entry.isBilled) {
    return (
      <Modal title="Time entry" description={`${formatDate(entry.date)} · ${entry.hours} hours · Billed`} onClose={onClose}>
        <div className="time-entry-review">
          <div className="time-entry-totals">
            <div><span>Hours</span><strong>{entry.hours}</strong></div>
            <div><span>Rate</span><strong>{entry.billableRate === null ? "—" : formatMoney(entry.billableRate)}</strong></div>
            <div><span>Amount</span><strong>{formatMoney(entry.amount ?? 0)}</strong></div>
          </div>
          <section><h3>{TIME_ENTRY_TYPES.find((item) => item.value === entry.type)?.label ?? entry.type}</h3><p>{entry.description}</p></section>
          <div className="time-entry-lock"><strong>Included on an invoice</strong><p>Billed time is locked to preserve the invoice and accounting history. Remove it from a Draft invoice first if it still needs correction.</p></div>
          <footer className="modal-actions"><button type="button" className="secondary-button" onClick={onClose}>Close</button></footer>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Edit time entry" description="Update or remove this unbilled entry before it reaches an invoice." onClose={onClose}>
      <form className="modal-form time-entry-form" onSubmit={(event) => void save(event)}>
        <div className="form-grid">
          <label className="field"><span>Work date</span><input required type="date" value={date} onChange={(event) => setDate(event.target.value)} /></label>
          <label className="field"><span>Hours</span><input required type="number" min="0.01" max="10000" step="0.01" value={hours} onChange={(event) => setHours(Number(event.target.value))} /></label>
          <label className="field"><span>Work type</span><select value={type} onChange={(event) => setType(event.target.value as TimeEntryType)}>{TIME_ENTRY_TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
          <div className="time-entry-amount"><span>Current amount</span><strong>{formatMoney(amount)}</strong><small>{entry.billableRate === null ? "No billing rate assigned" : `${formatMoney(entry.billableRate)} per hour`}</small></div>
          <label className="field field-wide"><span>Description</span><textarea required maxLength={10_000} value={description} onChange={(event) => setDescription(event.target.value)} /></label>
        </div>
        <label className="checkbox-field"><input type="checkbox" checked={isBillable} onChange={(event) => setIsBillable(event.target.checked)} /><span><strong>Billable</strong><small>Include this entry in unbilled work available for the next invoice.</small></span></label>
        {confirmDelete && <p className="time-entry-delete-warning">Delete this time entry permanently? Click “Confirm delete” to proceed.</p>}
        {error && <p className="form-error">{error}</p>}
        <footer className="modal-actions time-entry-actions">
          <button type="button" className="danger-button" disabled={saving || deleting} onClick={() => void remove()}>{deleting ? "Deleting…" : confirmDelete ? "Confirm delete" : "Delete entry"}</button>
          <span />
          <button type="button" className="secondary-button" disabled={saving || deleting} onClick={onClose}>Cancel</button>
          <button type="submit" className="primary-button" disabled={saving || deleting}>{saving ? "Saving…" : "Save changes"}</button>
        </footer>
      </form>
    </Modal>
  );
}

function EmptyWorkspace({
  hasMatters,
  openingMatter,
  onCreateMatter,
}: {
  hasMatters: boolean;
  openingMatter: boolean;
  onCreateMatter: () => void;
}) {
  return (
    <main className="empty-workspace">
      <div className="empty-mark">P+</div>
      <h2>{hasMatters ? "Select a matter" : "Open your first matter"}</h2>
      <p>{hasMatters
        ? "Choose a matter from the rail to open its conversations, files, and virtual employees."
        : "Create a client and matter workspace, then keep every conversation, file, and decision together."}</p>
      <button className="primary-button empty-action" onClick={onCreateMatter} disabled={openingMatter}>
        {openingMatter ? "Loading…" : "＋ New matter"}
      </button>
    </main>
  );
}

function AgentSkillsDialog({ onClose }: { onClose: () => void }) {
  const empty: Omit<AgentSkill, "updatedAt"> = {
    slug: "", name: "", description: "", category: "firm", instructions: "", enabled: true,
  };
  const [skills, setSkills] = useState<AgentSkill[]>([]);
  const [draft, setDraft] = useState<Omit<AgentSkill, "updatedAt">>(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mobileSkillsView, setMobileSkillsView] = useState<"list" | "editor">("list");

  useEffect(() => {
    api.agentSkills().then((result) => setSkills(result.skills)).catch((caught: Error) => setError(caught.message));
  }, []);

  const edit = async (skill: AgentSkill) => {
    setError(null);
    setMobileSkillsView("editor");
    try {
      const result = await api.agentSkill(skill.slug);
      setDraft({ ...result.skill });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The skill could not be loaded.");
    }
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const result = await api.saveAgentSkill(draft);
      setSkills((current) => [...current.filter((item) => item.slug !== result.skill.slug), result.skill]
        .sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name)));
      setDraft({ ...result.skill });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The skill could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal className="agent-skills-modal" title="Agent Skills" description="Firm-controlled playbooks that ParaPlus and every specialist load on demand." onClose={onClose}>
      <div className={`skills-layout mobile-skills-${mobileSkillsView}`}>
        <aside className="skills-list">
          <button className="secondary-button" onClick={() => {
            setDraft(empty);
            setMobileSkillsView("editor");
          }}>＋ New skill</button>
          {skills.map((skill) => (
            <button key={skill.slug} className={skill.slug === draft.slug ? "active" : ""} onClick={() => void edit(skill)}>
              <strong>{skill.name}</strong><span>{skill.category.replace("_", " ")} · {skill.enabled ? "active" : "archived"}</span>
            </button>
          ))}
        </aside>
        <form className="skill-form" onSubmit={save}>
          <button type="button" className="mobile-skills-back" onClick={() => setMobileSkillsView("list")}>← All skills</button>
          <label className="field"><span>Name</span><input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} required /></label>
          <label className="field"><span>Slug</span><input value={draft.slug} onChange={(event) => setDraft({ ...draft, slug: event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="ohio-state-courts" required /></label>
          <label className="field"><span>Category</span><select value={draft.category} onChange={(event) => setDraft({ ...draft, category: event.target.value as AgentSkillCategory })}><option value="jurisdiction">Jurisdiction</option><option value="court">Court</option><option value="practice_area">Practice area</option><option value="firm">Firm</option></select></label>
          <label className="field"><span>Activation description</span><textarea value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} required /></label>
          <label className="field"><span>Playbook instructions</span><textarea className="skill-instructions" value={draft.instructions} onChange={(event) => setDraft({ ...draft, instructions: event.target.value })} required /></label>
          <label className="checkbox-field"><input type="checkbox" checked={draft.enabled} onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })} /><span><strong>Active</strong><small>Active skills are discoverable by all ParaPlus agents.</small></span></label>
          {error && <p className="form-error">{error}</p>}
          <footer className="modal-actions"><button className="primary-button" disabled={saving}>{saving ? "Saving…" : "Save skill"}</button></footer>
        </form>
      </div>
    </Modal>
  );
}

function ChannelWorkspace({
  currentLawyerId,
  matter,
  matters,
  channel,
  channels,
  onSelectMatter,
  onSelectChannel,
  onChannelActivity,
  onSearch,
  onCreateMatter,
  onCreateChannel,
  onOpenTools,
  onOpenSkills,
  canDeleteChannel,
  onDeleteChannel,
}: {
  currentLawyerId: string;
  matter: Matter;
  matters: Matter[];
  channel: Channel;
  channels: Channel[];
  onSelectMatter: (matterId: string) => void;
  onSelectChannel: (channelId: string) => void;
  onChannelActivity: () => void;
  onSearch: () => void;
  onCreateMatter: () => void;
  onCreateChannel: () => void;
  onOpenTools: () => void;
  onOpenSkills: () => void;
  canDeleteChannel: boolean;
  onDeleteChannel: () => void;
}) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [agents, setAgents] = useState<AgentDefinition[]>([]);
  const [mentionableUsers, setMentionableUsers] = useState<MentionableUser[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [filesDialogOpen, setFilesDialogOpen] = useState(false);
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [approvalActionId, setApprovalActionId] = useState<string | null>(null);
  const [approvalNotice, setApprovalNotice] = useState<{ status: "completed" | "rejected"; message: string } | null>(null);
  const [workflows, setWorkflows] = useState<WorkflowRun[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [timeEntryOpen, setTimeEntryOpen] = useState<TimeEntry | null>(null);
  const [invoiceReview, setInvoiceReview] = useState<InvoiceReview | null>(null);
  const [invoiceLoadingId, setInvoiceLoadingId] = useState<string | null>(null);
  const [summary, setSummary] = useState<ChannelSummary | null>(null);
  const [presence, setPresence] = useState<Array<{ lawyerId: string; name: string }>>([]);
  const [streaming, setStreaming] = useState<Record<string, {
    agentId: string;
    text: string;
    status?: string;
    delegation?: {
      specialist: string;
      message: string;
      fraction: number | null;
      milestones?: string[];
    };
  }>>({});
  const [body, setBody] = useState("");
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<Document[]>([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(() => !window.matchMedia("(max-width: 1050px)").matches);
  const [mobileView, setMobileView] = useState<"chat" | "files" | "approvals" | "matter">("chat");
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [error, setError] = useState<string | null>(null);
  const [messageLoadError, setMessageLoadError] = useState<string | null>(null);
  const [sidebarLoadError, setSidebarLoadError] = useState<string | null>(null);
  const [knowledgeQuery, setKnowledgeQuery] = useState("");
  const [knowledgeScope, setKnowledgeScope] = useState<"matter" | "prior">("matter");
  const [knowledgeResults, setKnowledgeResults] = useState<KnowledgeResult[]>([]);
  const [knowledgeSearching, setKnowledgeSearching] = useState(false);
  const [launchingWorkflow, setLaunchingWorkflow] = useState<WorkflowType | null>(null);
  const [workflowActionId, setWorkflowActionId] = useState<string | null>(null);
  const [specialistPickerOpen, setSpecialistPickerOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const sidebarRefreshRef = useRef<{ key: string; promise: Promise<void> } | null>(null);
  const connectedChannelRef = useRef<string | null>(null);

  // An agent turn can add documents (saveDocumentFromText, uploadDocument,
  // formatDocument). Pull just the file list when a run ends so the result
  // appears on its own — previously it took a full page reload to show up.
  const refreshDocuments = useCallback(async (): Promise<void> => {
    try {
      const result = await api.documents(matter.id);
      setDocuments(result.documents);
    } catch {
      // Non-fatal — the list still refreshes on reconnect and channel switch.
    }
  }, [matter.id]);

  const refreshSidebar = useCallback((): Promise<void> => {
    const key = `${matter.id}:${channel.id}`;
    if (sidebarRefreshRef.current?.key === key) return sidebarRefreshRef.current.promise;

    const request = { key, promise: Promise.resolve() };
    request.promise = (async () => {
      const [agentResult, mentionResult, documentResult, summaryResult, workflowResult, invoiceResult, timeEntryResult] = await Promise.allSettled([
        api.agents(channel.id),
        api.mentionableUsers(channel.id),
        api.documents(matter.id),
        api.summary(channel.id),
        api.workflows(matter.id),
        api.invoices(matter.id),
        api.timeEntries(matter.id),
      ]);
      // Approval state crosses the Durable Object boundary. Load it after the
      // direct PostgreSQL reads so it cannot compete with the critical matter
      // data during the initial connection burst.
      const [approvalResult] = await Promise.allSettled([api.approvals(channel.id)]);
      if (sidebarRefreshRef.current !== request) return;
      const failures: string[] = [];
      if (agentResult.status === "fulfilled") setAgents(agentResult.value.agents);
      else failures.push("specialists");
      if (mentionResult.status === "fulfilled") setMentionableUsers(mentionResult.value.users);
      else failures.push("teammates");
      if (documentResult.status === "fulfilled") setDocuments(documentResult.value.documents);
      else failures.push("files");
      if (approvalResult.status === "fulfilled") setApprovals(approvalResult.value.approvals);
      else failures.push("approvals");
      if (summaryResult.status === "fulfilled") setSummary(summaryResult.value.summary);
      else failures.push("briefing");
      if (workflowResult.status === "fulfilled") setWorkflows(workflowResult.value.workflows);
      else failures.push("workflows");
      if (invoiceResult.status === "fulfilled") setInvoices(invoiceResult.value.invoices);
      else failures.push("invoices");
      if (timeEntryResult.status === "fulfilled") setTimeEntries(timeEntryResult.value.timeEntries);
      else failures.push("time entries");
      setSidebarLoadError(failures.length > 0
        ? `Some matter details could not be loaded: ${failures.join(", ")}.`
        : null);
    })().finally(() => {
      if (sidebarRefreshRef.current === request) sidebarRefreshRef.current = null;
    });
    sidebarRefreshRef.current = request;
    return request.promise;
  }, [channel.id, matter.id]);

  const refreshMessages = useCallback(async () => {
    try {
      const result = await api.messages(channel.id);
      setMessages(result.messages);
      setMessageLoadError(null);
      const last = result.messages.at(-1);
      await api.markRead(channel.id, last?.id ?? null);
      onChannelActivity();
    } catch (caught) {
      setMessageLoadError(caught instanceof Error ? caught.message : "Messages could not be loaded.");
    }
  }, [channel.id, onChannelActivity]);

  useEffect(() => {
    const setConnected = () => setOnline(true);
    const setDisconnected = () => setOnline(false);
    window.addEventListener("online", setConnected);
    window.addEventListener("offline", setDisconnected);
    return () => {
      window.removeEventListener("online", setConnected);
      window.removeEventListener("offline", setDisconnected);
    };
  }, []);

  useEffect(() => {
    setMessages([]);
    setStreaming({});
    setError(null);
    setMessageLoadError(null);
    setSidebarLoadError(null);
    setApprovalNotice(null);
    void refreshMessages();
    void refreshSidebar();
  }, [channel.id, refreshMessages, refreshSidebar]);

  const handleRealtime = useCallback((event: RealtimeEvent) => {
    if (event.type === "message.created" || event.type === "message.updated" || event.type === "reaction.changed") {
      setMessages((current) => upsertMessage(current, event.message));
      if (event.type === "message.created") {
        setStreaming({});
        void api.markRead(channel.id, event.message.id)
          .then(() => onChannelActivity())
          .catch((caught: Error) => setError(caught.message));
      }
    } else if (event.type === "message.deleted") {
      setMessages((current) => current.filter((message) => message.id !== event.messageId));
    } else if (event.type === "agent.delta") {
      setStreaming((current) => ({
        ...current,
        [event.runId]: {
          ...current[event.runId],
          agentId: event.agentId,
          text: (current[event.runId]?.text ?? "") + event.delta,
        },
      }));
    } else if (event.type === "agent.progress") {
      setStreaming((current) => ({
        ...current,
        [event.runId]: {
          ...current[event.runId],
          agentId: event.agentId,
          text: current[event.runId]?.text ?? "",
          delegation: {
            specialist: event.specialist,
            message: event.message,
            fraction: event.fraction,
          },
        },
      }));
    } else if (event.type === "agent.approval") {
      setApprovals((current) => [...current.filter((item) => item.id !== event.approval.id), event.approval]);
    } else if (event.type === "presence") {
      setPresence(event.users);
    } else if (event.type === "mini-app.updated") {
      setMessages((current) => current.map((message) => ({
        ...message,
        miniApps: message.miniApps.map((app) => app.id === event.miniApp.id ? event.miniApp : app),
      })));
    } else if (event.type === "workflow.updated") {
      setWorkflows((current) => upsertWorkflow(current, event.workflow));
    } else if (event.type === "agent.run") {
      if (event.run.status === "queued" || event.run.status === "running") {
        setStreaming((current) => ({
          ...current,
          [event.run.id]: {
            ...current[event.run.id],
            agentId: event.run.agentId,
            text: current[event.run.id]?.text ?? "",
            status: event.run.status === "queued" ? "Starting…" : "Working…",
          },
        }));
      } else {
        setStreaming((current) => {
          const next = { ...current };
          delete next[event.run.id];
          return next;
        });
        void refreshDocuments();
        if (event.run.status === "failed") {
          setError("The virtual employee could not complete that request. Please try again.");
        }
      }
    }
  }, [channel.id, onChannelActivity, refreshDocuments]);

  const roomAgent = useAgent({
    agent: "ChannelRoom",
    name: channel.id,
    onOpen: () => {
      const isInitialConnection = connectedChannelRef.current !== channel.id;
      connectedChannelRef.current = channel.id;
      if (!isInitialConnection) void refreshSidebar();
    },
    onMessage: (event) => {
      if (typeof event.data !== "string" || event.data === "pong") return;
      try {
        handleRealtime(JSON.parse(event.data) as RealtimeEvent);
      } catch {
        // Ignore non-event Agent protocol frames.
      }
    },
  });
  const agentToolEvents = useAgentToolEvents({ agent: roomAgent });
  const displayedStreaming = useMemo(() => {
    const next = { ...streaming };
    for (const run of Object.values(agentToolEvents.runsById)) {
      if (run.status !== "running") continue;
      const paraPlusRunId = typeof run.display?.paraPlusRunId === "string"
        ? run.display.paraPlusRunId
        : null;
      const paraPlusAgentId = typeof run.display?.paraPlusAgentId === "string"
        ? run.display.paraPlusAgentId
        : null;
      if (!paraPlusRunId || !paraPlusAgentId) continue;
      next[paraPlusRunId] = {
        ...next[paraPlusRunId],
        agentId: paraPlusAgentId,
        text: next[paraPlusRunId]?.text ?? "",
        delegation: {
          specialist: run.display?.name ?? run.agentType,
          message: run.progress?.message ?? "The retained specialist run is active.",
          fraction: run.progress?.fraction ?? null,
          milestones: run.milestones?.map((milestone) => milestone.name) ?? [],
        },
      };
    }
    return next;
  }, [agentToolEvents.runsById, streaming]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
    const messageId = new URL(window.location.href).searchParams.get("message");
    if (messageId && messages.some((message) => message.id === messageId)) {
      window.requestAnimationFrame(() => document.getElementById(`message-${messageId}`)?.scrollIntoView({ block: "center" }));
    }
  }, [displayedStreaming, messages]);

  const send = async () => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    setError(null);
    try {
      const result = await api.sendMessage(channel.id, {
        body: text,
        clientNonce: crypto.randomUUID(),
        documentIds: attachments.map((document) => document.id),
        mentionedLawyerIds: mentionedUserIds,
      });
      setMessages((current) => upsertMessage(current, result.message));
      setBody("");
      setAttachments([]);
      setMentionedUserIds([]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Message failed to send.");
    } finally {
      setSending(false);
    }
  };

  const upload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const result = await api.uploadDocument(matter.id, file);
      setDocuments((current) => [result.document, ...current]);
      setAttachments((current) => [...current, result.document]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const uploadFiles = async (files: FileList) => {
    for (const file of Array.from(files)) await upload(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const searchKnowledge = async () => {
    if (knowledgeQuery.trim().length < 2) return;
    setKnowledgeSearching(true);
    setError(null);
    try {
      const result = knowledgeScope === "matter"
        ? await api.searchMatterKnowledge(matter.id, knowledgeQuery)
        : await api.searchPriorWorkProduct(knowledgeQuery, matter.id);
      setKnowledgeResults(result.results);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Knowledge search failed.");
    } finally {
      setKnowledgeSearching(false);
    }
  };

  const designatePriorWork = async (document: Document) => {
    try {
      const result = await api.setPriorWorkProduct(document.id, !document.isPriorWorkProduct);
      setDocuments((current) => current.map((item) => item.id === result.document.id ? result.document : item));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The work-product designation could not be changed.");
    }
  };

  const mention = (agent: AgentDefinition) => {
    const mentionName = agentMentionName(agent);
    setBody((current) => `${current}${current && !current.endsWith(" ") ? " " : ""}@${mentionName} `);
    setSpecialistPickerOpen(false);
  };

  const mentionMatch = body.match(/(?:^|\s)@([^@\n]{0,80})$/);
  const mentionQuery = mentionMatch?.[1].trim().toLocaleLowerCase() ?? null;
  const mentionSuggestions = mentionQuery === null
    ? []
    : [
      ...agents
        .filter((agent) => {
          const searchable = `${agentMentionName(agent)} ${agent.name} ${agent.description}`.toLocaleLowerCase();
          return searchable.includes(mentionQuery);
        })
        .sort((left, right) => Number(right.slug === "paraplus") - Number(left.slug === "paraplus"))
        .map((agent) => ({ kind: "agent" as const, agent })),
      ...mentionableUsers
        .filter((user) => (
          user.displayName.toLocaleLowerCase().includes(mentionQuery)
          || user.email.toLocaleLowerCase().includes(mentionQuery)
        ))
        .map((user) => ({ kind: "user" as const, user })),
    ].slice(0, 10);

  const updateBody = (value: string) => {
    setBody(value);
    setMentionedUserIds((current) => current.filter((lawyerId) => {
      const user = mentionableUsers.find((candidate) => candidate.id === lawyerId);
      return user ? value.toLocaleLowerCase().includes(`@${user.displayName}`.toLocaleLowerCase()) : false;
    }));
  };

  const mentionUser = (user: MentionableUser) => {
    const match = body.match(/(?:^|\s)@([^@\n]{0,80})$/);
    if (!match || match.index === undefined) return;
    const atIndex = match.index + match[0].indexOf("@");
    setBody(`${body.slice(0, atIndex)}@${user.displayName} `);
    setMentionedUserIds((current) => current.includes(user.id) ? current : [...current, user.id]);
  };

  const selectMentionSuggestion = (suggestion: (typeof mentionSuggestions)[number]) => {
    if (suggestion.kind === "user") {
      mentionUser(suggestion.user);
      return;
    }
    const match = body.match(/(?:^|\s)@([^@\n]{0,80})$/);
    if (!match || match.index === undefined) return;
    const atIndex = match.index + match[0].indexOf("@");
    setBody(`${body.slice(0, atIndex)}@${agentMentionName(suggestion.agent)} `);
  };

  const paraPlusAgent = agents.find((agent) => agent.slug === "paraplus") ?? null;
  const specialistAgents = agents.filter((agent) => agent.slug !== "paraplus");

  const decide = async (approval: Approval, decision: "approve" | "reject") => {
    if (approvalActionId) return;
    setApprovalActionId(approval.id);
    setError(null);
    setApprovalNotice(null);
    try {
      const result = await api.decideApproval(channel.id, approval.id, decision);
      setApprovals((current) => current.filter((item) => item.id !== approval.id));
      setApprovalNotice({
        status: result.approval.status,
        message: result.approval.confirmationReady
          ? result.approval.message
          : `${result.approval.message} The virtual employee's follow-up is still being prepared.`,
      });
      await Promise.allSettled([refreshMessages(), refreshSidebar()]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Approval could not be updated.");
    } finally {
      setApprovalActionId(null);
    }
  };

  const startWorkflow = async (workflowType: WorkflowType) => {
    if (launchingWorkflow) return;
    setLaunchingWorkflow(workflowType);
    setError(null);
    try {
      const result = await api.startWorkflow(channel.id, workflowType);
      setWorkflows((current) => upsertWorkflow(current, result.workflow));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Workflow could not be started.");
    } finally {
      setLaunchingWorkflow(null);
    }
  };

  const decideWorkflow = async (workflow: WorkflowRun, decision: "approve" | "reject") => {
    if (workflowActionId) return;
    setWorkflowActionId(workflow.id);
    setError(null);
    try {
      await api.decideWorkflow(workflow.id, decision);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Workflow approval could not be updated.");
    } finally {
      setWorkflowActionId(null);
    }
  };

  const actOnWorkflow = async (workflow: WorkflowRun, action: "retry" | "cancel") => {
    if (workflowActionId) return;
    setWorkflowActionId(workflow.id);
    setError(null);
    try {
      const result = await api.workflowAction(workflow.id, action);
      setWorkflows((current) => upsertWorkflow(current, result.workflow));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : `Workflow could not be ${action === "retry" ? "retried" : "cancelled"}.`);
    } finally {
      setWorkflowActionId(null);
    }
  };

  const openInvoice = async (invoice: Invoice) => {
    if (invoiceLoadingId) return;
    setInvoiceLoadingId(invoice.id);
    setError(null);
    try {
      setInvoiceReview(await api.invoiceReview(invoice.id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The invoice could not be opened.");
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  const invoiceUpdated = (review: InvoiceReview) => {
    setInvoices((current) => current.map((item) => item.id === review.invoice.id ? review.invoice : item));
    setInvoiceReview(review);
  };

  const timeEntryUpdated = (entry: TimeEntry) => {
    setTimeEntries((current) => current.map((item) => item.id === entry.id ? entry : item));
  };

  const timeEntryDeleted = (entryId: string) => {
    setTimeEntries((current) => current.filter((item) => item.id !== entryId));
  };

  const activeWorkflowStatuses = new Set<WorkflowRun["status"]>(["queued", "running", "awaiting_approval"]);
  const activeWorkflowTypes = new Set(
    workflows.filter((workflow) => activeWorkflowStatuses.has(workflow.status)).map((workflow) => workflow.workflowType),
  );
  const visibleWorkflows = [
    ...workflows.filter((workflow) => activeWorkflowStatuses.has(workflow.status)),
    ...workflows.filter((workflow) => !activeWorkflowStatuses.has(workflow.status)).slice(0, 5),
  ];

  return (
    <main className={`workspace mobile-view-${mobileView}`}>
      {!online && <div className="offline-banner" role="status">You are offline. Reconnect before sending messages or opening secure matter data.</div>}
      <header className="channel-header">
        <div>
          <div className="channel-title"><span>#</span>{channel.name}</div>
          <select className="mobile-matter-select" value={matter.id} onChange={(event) => onSelectMatter(event.target.value)} aria-label="Matter">
            {matters.map((item) => <option value={item.id} key={item.id}>{item.matterNumber} · {item.name}</option>)}
          </select>
          <select className="mobile-channel-select" value={channel.id} onChange={(event) => onSelectChannel(event.target.value)} aria-label="Channel">
            {channels.map((item) => <option value={item.id} key={item.id}># {item.name}</option>)}
          </select>
          <p>{channel.description ?? `${matter.matterNumber} · ${matter.name}`}</p>
        </div>
        <div className="header-actions">
          <span className="presence-pill"><i />{presence.length || 1} here</span>
          <button className="mobile-header-action" onClick={onSearch} aria-label="Search matter">⌕</button>
          <button className="mobile-header-action" onClick={onOpenTools} aria-label="Open firm tools"><FirmToolsIcon /></button>
          <button
            className="mobile-header-action"
            onClick={() => setMobileActionsOpen((value) => !value)}
            aria-label="More workspace actions"
            aria-expanded={mobileActionsOpen}
          >•••</button>
          <button className="icon-button details-toggle" onClick={() => setDetailsOpen((value) => !value)} aria-label="Toggle matter details">◫</button>
        </div>
      </header>
      {mobileActionsOpen && (
        <>
          <button className="mobile-actions-backdrop" aria-label="Close workspace actions" onClick={() => setMobileActionsOpen(false)} />
          <div className="mobile-actions-menu" role="menu" aria-label="Workspace actions">
            <button role="menuitem" onClick={() => {
              setMobileActionsOpen(false);
              onCreateMatter();
            }}><span>＋</span><strong>New matter</strong></button>
            <button role="menuitem" onClick={() => {
              setMobileActionsOpen(false);
              onCreateChannel();
            }}><span>#</span><strong>New channel</strong></button>
            <button role="menuitem" onClick={() => {
              setMobileActionsOpen(false);
              onOpenSkills();
            }}><span>S</span><strong>Agent Skills</strong></button>
          </div>
        </>
      )}

      <div className="channel-body">
        <section className="conversation">
          <div className="message-list" ref={listRef}>
            <div className="channel-intro">
              <div className="channel-symbol">#</div>
              <h2>Welcome to #{channel.name}</h2>
              <p>This is the beginning of the shared record. Mention @ParaPlus to work with any firm capability from this channel.</p>
            </div>
            {(messageLoadError || sidebarLoadError) && (
              <div className="workspace-load-warning" role="alert">
                <span>
                  <strong>Part of this workspace did not load.</strong>
                  {messageLoadError && <small>Messages: {messageLoadError}</small>}
                  {sidebarLoadError && <small>{sidebarLoadError}</small>}
                </span>
                <button type="button" onClick={() => {
                  void refreshMessages();
                  void refreshSidebar();
                }}>Retry</button>
              </div>
            )}
            {messages.map((message) => (
              <article
                id={`message-${message.id}`}
                className={`message ${message.authorType === "agent" ? "agent-message" : ""}${message.mentionedCurrentUser || message.mentions.some((mention) => mention.lawyerId === currentLawyerId) ? " mentioned" : ""}`}
                key={message.id}
              >
                <Avatar label={message.authorAvatarLabel} agent={message.authorType === "agent"} />
                <div className="message-content">
                  <div className="message-meta">
                    <strong>{message.authorName}</strong>
                    {message.authorType === "agent" && <span className="employee-badge">VIRTUAL EMPLOYEE</span>}
                    <time>{formatTime(message.createdAt)}</time>
                    {message.editedAt && <span>edited</span>}
                  </div>
                  <MessageBody message={message} />
                  {message.attachments.length > 0 && (
                    <div className="attachment-grid">
                      {message.attachments.map((attachment) => (
                        <a href={`/api/documents/${attachment.documentId}/download`} className="attachment-card" key={attachment.documentId}>
                          <span className="file-icon">DOC</span>
                          <span><strong>{attachment.fileName}</strong><small>{formatBytes(attachment.fileSizeBytes)}</small></span>
                        </a>
                      ))}
                    </div>
                  )}
                  {message.miniApps.map((app) => <MiniAppCard key={app.id} app={app} onUpdate={(updated) => setMessages((current) => current.map((item) => ({ ...item, miniApps: item.miniApps.map((candidate) => candidate.id === updated.id ? updated : candidate) })))} />)}
                </div>
              </article>
            ))}
            {Object.entries(displayedStreaming).map(([runId, stream]) => {
              const activeAgent = agents.find((agent) => agent.id === stream.agentId);
              return (
                <article className="message agent-message streaming" key={runId}>
                  <Avatar label={activeAgent?.avatarLabel ?? "AI"} agent />
                  <div className="message-content">
                    <div className="message-meta"><strong>{activeAgent?.name ?? "Virtual employee"}</strong><span className="employee-badge">WORKING</span></div>
                    {stream.delegation && (
                      <div className="specialist-progress" role="status">
                        <span>Specialist engaged</span>
                        <strong>{stream.delegation.specialist}</strong>
                        <small>{stream.delegation.message}</small>
                        {stream.delegation.fraction !== null && (
                          <i><b style={{ width: `${Math.round(stream.delegation.fraction * 100)}%` }} /></i>
                        )}
                        {stream.delegation.milestones && stream.delegation.milestones.length > 0 && (
                          <div className="specialist-milestones" aria-label="Completed specialist milestones">
                            {stream.delegation.milestones.slice(-3).map((milestone) => (
                              <span key={milestone}>{milestone.replaceAll("-", " ")}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="message-body">{stream.text || stream.status || (stream.delegation ? "Working with the specialist…" : "Working…")}<span className="cursor" /></div>
                  </div>
                </article>
              );
            })}
            {approvals.length > 0 && (
              <section className="conversation-approvals" aria-label="Pending approvals">
                <header><span>Action required</span><strong>{approvals.length} pending approval{approvals.length === 1 ? "" : "s"}</strong></header>
                {approvals.map((approval) => (
                  <div className="conversation-approval-card" key={approval.id}>
                    <div><strong>{approval.capabilityName}</strong><p>{approval.summary}</p></div>
                    <details><summary>Review exactly what will run</summary><pre>{JSON.stringify(approval.capabilityInput, null, 2)}</pre></details>
                    <footer>
                      <button disabled={approvalActionId !== null} onClick={() => void decide(approval, "reject")}>Reject</button>
                      <button disabled={approvalActionId !== null} className="approve" onClick={() => void decide(approval, "approve")}>{approvalActionId === approval.id ? "Running…" : "Approve and run"}</button>
                    </footer>
                  </div>
                ))}
              </section>
            )}
            {approvalNotice && (
              <div className={`approval-notice ${approvalNotice.status}`} role="status">
                <div><strong>{approvalNotice.status === "completed" ? "Action completed" : "Action rejected"}</strong><span>{approvalNotice.message}</span></div>
                <button onClick={() => setApprovalNotice(null)} aria-label="Dismiss approval result">×</button>
              </div>
            )}
          </div>

          <div className="composer-wrap">
            {error && <div className="inline-error">{error}<button onClick={() => setError(null)}>×</button></div>}
            {attachments.length > 0 && (
              <div className="composer-files">
                {attachments.map((document) => (
                  <span key={document.id}>{document.fileName}<button onClick={() => setAttachments((items) => items.filter((item) => item.id !== document.id))}>×</button></span>
                ))}
              </div>
            )}
            <div className="composer">
              <textarea
                value={body}
                onChange={(event) => updateBody(event.target.value)}
                onKeyDown={(event) => {
                  if ((event.key === "Enter" || event.key === "Tab") && mentionSuggestions.length > 0) {
                    event.preventDefault();
                    selectMentionSuggestion(mentionSuggestions[0]);
                    return;
                  }
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void send();
                  }
                }}
                placeholder={`Message #${channel.name} or ask @ParaPlus`}
                rows={2}
              />
              {mentionSuggestions.length > 0 && (
                <div className="mention-suggestions" role="listbox" aria-label="Mention a teammate or specialist">
                  {mentionSuggestions.map((suggestion) => (
                    <button
                      key={suggestion.kind === "agent" ? `agent-${suggestion.agent.id}` : `user-${suggestion.user.id}`}
                      type="button"
                      role="option"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => selectMentionSuggestion(suggestion)}
                    >
                      {suggestion.kind === "agent" ? (
                        <>
                          <Avatar label={suggestion.agent.avatarLabel} agent />
                          <span>
                            <strong>@{agentMentionName(suggestion.agent)}{suggestion.agent.slug === "paraplus" && <em>DEFAULT</em>}</strong>
                            <small>{suggestion.agent.slug === "paraplus" ? "Ask anything; ParaPlus routes work automatically." : `Optional specialist · ${suggestion.agent.description}`}</small>
                          </span>
                        </>
                      ) : (
                        <>
                          <Avatar label={initials(suggestion.user.displayName)} />
                          <span><strong>{suggestion.user.displayName}</strong><small>{suggestion.user.role} · {suggestion.user.email}</small></span>
                        </>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {specialistPickerOpen && (
                <div className="specialist-picker">
                  <header>
                    <span><strong>Optional specialists</strong><small>ParaPlus already routes work automatically. Choose one only to force a specific focus.</small></span>
                    <button type="button" aria-label="Close specialists" onClick={() => setSpecialistPickerOpen(false)}>×</button>
                  </header>
                  {specialistAgents.map((agent) => (
                    <button type="button" key={agent.id} onClick={() => mention(agent)}>
                      <Avatar label={agent.avatarLabel} agent />
                      <span><strong>@{agentMentionName(agent)}</strong><small>{agent.description}</small></span>
                    </button>
                  ))}
                </div>
              )}
              <div className="composer-toolbar">
                <div>
                  <button className="tool-button" onClick={() => fileRef.current?.click()} disabled={uploading || !online}>＋ <span>{uploading ? "Uploading…" : "Attach"}</span></button>
                  <input ref={fileRef} type="file" multiple hidden onChange={(event) => event.target.files && void uploadFiles(event.target.files)} />
                  {paraPlusAgent && (
                    <button className="agent-chip default-agent" onClick={() => mention(paraPlusAgent)}>
                      <Avatar label={paraPlusAgent.avatarLabel} agent /> Ask ParaPlus
                    </button>
                  )}
                  {specialistAgents.length > 0 && (
                    <button
                      className={`specialist-picker-toggle${specialistPickerOpen ? " active" : ""}`}
                      type="button"
                      aria-expanded={specialistPickerOpen}
                      onClick={() => setSpecialistPickerOpen((value) => !value)}
                    >
                      Specialists <span>⌄</span>
                    </button>
                  )}
                </div>
                <button className="send-button" onClick={() => void send()} disabled={!body.trim() || sending || !online} aria-label="Send message">↑</button>
              </div>
            </div>
            <p className="composer-hint">Type @ to mention teammates or virtual employees · Enter to send · Shift + Enter for a new line</p>
          </div>
        </section>

        {detailsOpen && <button className="details-backdrop" aria-label="Close matter details" onClick={() => setDetailsOpen(false)} />}
        <aside className={`details-panel${detailsOpen ? " open" : ""}`}>
            <section className="detail-section knowledge-search files-detail">
              <div className="detail-heading"><h4>AI document search</h4></div>
              <div className="knowledge-scope"><button className={knowledgeScope === "matter" ? "active" : ""} onClick={() => setKnowledgeScope("matter")}>This matter</button><button className={knowledgeScope === "prior" ? "active" : ""} onClick={() => setKnowledgeScope("prior")}>Prior work</button></div>
              <form onSubmit={(event) => { event.preventDefault(); void searchKnowledge(); }}><input value={knowledgeQuery} onChange={(event) => setKnowledgeQuery(event.target.value)} placeholder={knowledgeScope === "matter" ? "Search every matter file" : "Search approved prior work"} /><button disabled={knowledgeSearching || knowledgeQuery.trim().length < 2}>{knowledgeSearching ? "…" : "Search"}</button></form>
              {knowledgeResults.map((result) => <a href={`/api/documents/${result.documentId}/download`} className="knowledge-result" key={result.chunkId}><strong>{result.fileName}</strong><span>{result.text}</span></a>)}
              {!knowledgeSearching && knowledgeQuery && knowledgeResults.length === 0 && <p className="muted">No matching document passages yet.</p>}
            </section>

            <section className="detail-section files-section files-detail">
              <div className="detail-heading">
                <h4>Matter files</h4>
                <div className="files-heading-actions"><span>{documents.length}</span><button type="button" onClick={() => setFilesDialogOpen(true)}>Browse</button></div>
              </div>
              {documents.slice(0, 8).map((document) => (
                <div className="file-row-wrap" key={document.id}>
                  <a className="file-row" href={`/api/documents/${document.id}/download`}>
                    <span className="file-icon">{document.fileName.split(".").pop()?.slice(0, 3).toUpperCase() ?? "DOC"}</span>
                    <span><strong>{document.fileName}</strong><small>{formatBytes(document.fileSizeBytes)} · {document.type}</small></span>
                  </a>
                  <button onClick={() => void designatePriorWork(document)} title={document.isPriorWorkProduct ? "Remove from prior work product" : "Approve as reusable prior work product"}>{document.isPriorWorkProduct ? "Reusable" : "Mark reusable"}</button>
                </div>
              ))}
              {documents.length === 0 && <p className="muted">No files have been added yet.</p>}
            </section>

            {summary && (
              <section className="detail-section briefing matter-detail">
                <div className="detail-heading"><h4>Channel briefing</h4></div>
                <p>{summary.summary}</p>
                {summary.decisions.length > 0 && <><strong>Decisions</strong><ul>{summary.decisions.map((item) => <li key={item}>{item}</li>)}</ul></>}
                {summary.openQuestions.length > 0 && <><strong>Open questions</strong><ul>{summary.openQuestions.map((item) => <li key={item}>{item}</li>)}</ul></>}
              </section>
            )}

            {canDeleteChannel && (
              <section className="detail-section channel-admin-detail matter-detail">
                <div className="detail-heading"><h4>Channel administration</h4></div>
                <p>
                  {channel.isDefault
                    ? "The default General channel is required and cannot be deleted."
                    : "Remove this channel from the matter workspace for everyone."}
                </p>
                <button
                  type="button"
                  className="danger-button"
                  disabled={channel.isDefault}
                  onClick={onDeleteChannel}
                >
                  Delete channel
                </button>
              </section>
            )}

            {approvals.length > 0 && (
              <section className="detail-section approvals approvals-detail">
                <div className="detail-heading"><h4>Needs approval</h4><span>{approvals.length}</span></div>
                {approvals.map((approval) => (
                  <div className="approval-card" key={approval.id}>
                    <strong>{approval.capabilityName}</strong>
                    <p>{approval.summary}</p>
                    <details><summary>Review inputs</summary><pre>{JSON.stringify(approval.capabilityInput, null, 2)}</pre></details>
                    <div><button disabled={approvalActionId !== null} onClick={() => void decide(approval, "reject")}>Reject</button><button disabled={approvalActionId !== null} className="approve" onClick={() => void decide(approval, "approve")}>{approvalActionId === approval.id ? "Working…" : "Approve"}</button></div>
                  </div>
                ))}
              </section>
            )}

            <section className="detail-section workflows approvals-detail">
              <div className="detail-heading"><h4>Matter workflows</h4><span>{workflows.length}</span></div>
              <div className="workflow-launchers">
                <button disabled={launchingWorkflow !== null || activeWorkflowTypes.has("intake")} onClick={() => void startWorkflow("intake")}>{launchingWorkflow === "intake" ? "Starting…" : "Intake"}</button>
                <button disabled={launchingWorkflow !== null || activeWorkflowTypes.has("conflict_check")} onClick={() => void startWorkflow("conflict_check")}>{launchingWorkflow === "conflict_check" ? "Starting…" : "Conflict check"}</button>
                <button disabled={launchingWorkflow !== null || activeWorkflowTypes.has("billing_run")} onClick={() => void startWorkflow("billing_run")}>{launchingWorkflow === "billing_run" ? "Starting…" : "Billing run"}</button>
                <button disabled={launchingWorkflow !== null || activeWorkflowTypes.has("trust_reconciliation")} onClick={() => void startWorkflow("trust_reconciliation")}>{launchingWorkflow === "trust_reconciliation" ? "Starting…" : "Trust reconciliation"}</button>
              </div>
              {visibleWorkflows.map((workflow) => (
                <div className="workflow-card" key={workflow.id}>
                  <div><strong>{workflow.workflowType.replaceAll("_", " ")}</strong><span>{workflow.status.replaceAll("_", " ")}</span></div>
                  {workflow.errorMessage && <p className="workflow-error">{workflow.errorMessage}</p>}
                  {workflow.status === "awaiting_approval" ? (
                    <div className="workflow-plan">
                      <strong>What this approval does</strong>
                      <p>{workflow.approvalDescription}</p>
                      <strong>Review plan</strong>
                      <ol>{workflow.checklist.map((item) => <li key={item}>{item}</li>)}</ol>
                      {Object.keys(workflow.input).length > 0 ? (
                        <details>
                          <summary>Inputs supplied to this review</summary>
                          <pre>{JSON.stringify(workflow.input, null, 2)}</pre>
                        </details>
                      ) : <small>No additional inputs were supplied.</small>}
                    </div>
                  ) : (
                    <details className="workflow-plan workflow-plan-collapsed">
                      <summary>View plan and approval scope</summary>
                      <p>{workflow.approvalDescription}</p>
                      <ol>{workflow.checklist.map((item) => <li key={item}>{item}</li>)}</ol>
                      {Object.keys(workflow.input).length > 0 && <pre>{JSON.stringify(workflow.input, null, 2)}</pre>}
                    </details>
                  )}
                  {workflow.status === "awaiting_approval" && (
                    <div className="workflow-actions">
                      <button disabled={workflowActionId !== null} onClick={() => void decideWorkflow(workflow, "reject")}>Reject</button>
                      <button disabled={workflowActionId !== null} className="approve" onClick={() => void decideWorkflow(workflow, "approve")}>Approve and start review</button>
                    </div>
                  )}
                  {(workflow.status === "queued" || workflow.status === "running") && (
                    <div className="workflow-actions">
                      <button disabled={workflowActionId !== null} onClick={() => void actOnWorkflow(workflow, "cancel")}>{workflowActionId === workflow.id ? "Cancelling…" : "Cancel"}</button>
                    </div>
                  )}
                  {(workflow.status === "failed" || workflow.status === "cancelled") && (
                    <div className="workflow-actions">
                      <button disabled={workflowActionId !== null || activeWorkflowTypes.has(workflow.workflowType)} className="approve" onClick={() => void actOnWorkflow(workflow, "retry")}>{workflowActionId === workflow.id ? "Retrying…" : "Retry"}</button>
                    </div>
                  )}
                </div>
              ))}
            </section>

        </aside>
      </div>
      <nav className="mobile-bottom-nav" aria-label="Workspace views">
        <button className={mobileView === "chat" ? "active" : ""} onClick={() => setMobileView("chat")}><span>◌</span>Chat</button>
        <button className={mobileView === "files" ? "active" : ""} onClick={() => setMobileView("files")}><span>▤</span>Files</button>
        <button className={mobileView === "approvals" ? "active" : ""} onClick={() => setMobileView("approvals")}><span>✓</span>Approvals{approvals.length > 0 && <b>{approvals.length}</b>}</button>
        <button className={mobileView === "matter" ? "active" : ""} onClick={() => setMobileView("matter")}><span>◇</span>Matter</button>
      </nav>
      {filesDialogOpen && (
        <MatterFilesDialog
          matter={matter}
          documents={documents}
          onClose={() => setFilesDialogOpen(false)}
          onDocumentsChange={setDocuments}
        />
      )}
      {invoiceReview && (
        <InvoiceReviewDialog
          review={invoiceReview}
          onClose={() => setInvoiceReview(null)}
          onUpdated={invoiceUpdated}
        />
      )}
      {timeEntryOpen && (
        <TimeEntryDialog
          entry={timeEntryOpen}
          onClose={() => setTimeEntryOpen(null)}
          onUpdated={timeEntryUpdated}
          onDeleted={timeEntryDeleted}
        />
      )}
    </main>
  );
}

function Login({ onSignedIn }: { onSignedIn: () => Promise<void> | void }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const requestCode = async () => {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const result = await api.requestLoginCode(email);
      setChallengeId(result.challengeId);
      setCode("");
      setMessage(result.message);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "ParaPlus could not send a sign-in code.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-shell">
      <section className="login-brand">
        <span className="login-eyebrow">PARAPLUS</span>
        <h1>Your legal practice, working as one.</h1>
        <p>Move matters forward with your team, firm knowledge, documents, billing, and trusted AI assistance in one secure workspace.</p>
        <div className="login-feature-grid">
          <span>Matter workspaces</span>
          <span>Firm knowledge</span>
          <span>Secure documents</span>
          <span>Audited actions</span>
        </div>
      </section>
      <section className="login-card">
        <img src={paraPlusLogo} alt="ParaPlus" />
        <span className="login-product">Secure firm sign-in</span>
        <h2>{challengeId ? "Check your email" : "Welcome back"}</h2>
        <p>
          {challengeId
            ? <>Enter the six-digit ParaPlus code sent to <strong>{email}</strong>.</>
            : "Use the email address provisioned by your firm administrator."}
        </p>
        {!challengeId ? (
          <form onSubmit={(event) => { event.preventDefault(); void requestCode(); }}>
            <label>
              Email address
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@yourfirm.com"
                autoComplete="email"
                required
              />
            </label>
            <button className="login-primary" disabled={busy}>
              {busy ? "Sending code…" : "Send my ParaPlus code"}
            </button>
          </form>
        ) : (
          <form onSubmit={async (event) => {
            event.preventDefault();
            setBusy(true);
            setError(null);
            try {
              await api.verifyLoginCode(challengeId, code);
              await onSignedIn();
            } catch (caught) {
              setError(caught instanceof Error ? caught.message : "That code was not accepted.");
            } finally {
              setBusy(false);
            }
          }}>
            <label>
              One-time code
              <input
                className="login-code-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                autoFocus
                required
              />
            </label>
            <button className="login-primary" disabled={busy || code.length !== 6}>
              {busy ? "Signing in…" : "Continue to ParaPlus"}
            </button>
            <div className="login-secondary-actions">
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setChallengeId(null);
                  setCode("");
                  setMessage(null);
                  setError(null);
                }}
              >
                Use a different email
              </button>
              <button type="button" disabled={busy} onClick={() => void requestCode()}>
                Send a new code
              </button>
            </div>
          </form>
        )}
        {message && <div className="login-notice success">{message}</div>}
        {error && <div className="login-notice error">{error}</div>}
        <small>Codes expire after 10 minutes and work once. ParaPlus will never ask you to share a code.</small>
      </section>
    </main>
  );
}

export function App() {
  const initialLocation = useRef(new URL(window.location.href));
  const initialChannelId = useRef(initialLocation.current.searchParams.get("channel"));
  const [lawyer, setLawyer] = useState<Lawyer | null>(null);
  const [matters, setMatters] = useState<Matter[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [matterId, setMatterId] = useState<string | null>(null);
  const [channelId, setChannelId] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [matterDialogOpen, setMatterDialogOpen] = useState(false);
  const [matterDialogLoading, setMatterDialogLoading] = useState(false);
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState<Channel | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [firmToolsOpen, setFirmToolsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadWorkspace = useCallback(async () => {
    setLoading(true);
    setFatal(null);
    try {
      const [session, matterResult] = await Promise.all([api.session(), api.matters()]);
      setLawyer(session.lawyer);
      setMatters(matterResult.matters);
      const requestedMatterId = initialLocation.current.searchParams.get("matter");
      setMatterId(matterResult.matters.some((matter) => matter.id === requestedMatterId)
        ? requestedMatterId
        : matterResult.matters[0]?.id ?? null);
    } catch (error) {
      setLawyer(null);
      setMatters([]);
      setChannels([]);
      setMatterId(null);
      setChannelId(null);
      const unauthorized =
        error instanceof Error &&
        "status" in error &&
        error.status === 401;
      if (!unauthorized) {
        setFatal(error instanceof Error ? error.message : "ParaPlus could not be opened.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  const refreshChannels = useCallback(async () => {
    if (!matterId) return;
    const result = await api.channels(matterId);
    setChannels(result.channels);
    setChannelId((current) => {
      const requested = initialChannelId.current;
      initialChannelId.current = null;
      if (requested && result.channels.some((channel) => channel.id === requested)) return requested;
      return result.channels.some((channel) => channel.id === current) ? current : result.channels[0]?.id ?? null;
    });
  }, [matterId]);

  useEffect(() => {
    setChannels([]);
    setChannelId(null);
    void refreshChannels().catch((error: Error) => setFatal(error.message));
  }, [refreshChannels]);

  useEffect(() => {
    if (!matterId || search.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = window.setTimeout(() => {
      void api.search(matterId, search).then((result) => setSearchResults(result.results));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [matterId, search]);

  const selectedMatter = useMemo(() => matters.find((matter) => matter.id === matterId) ?? null, [matterId, matters]);
  const selectedChannel = useMemo(() => channels.find((channel) => channel.id === channelId) ?? null, [channelId, channels]);

  useEffect(() => {
    if (!matterId) return;
    const next = new URL(window.location.href);
    next.searchParams.set("matter", matterId);
    if (channelId) next.searchParams.set("channel", channelId);
    else next.searchParams.delete("channel");
    window.history.replaceState(null, "", `${next.pathname}${next.search}${next.hash}`);
  }, [matterId, channelId]);

  const openMatterDialog = async () => {
    if (matterDialogLoading) return;
    setMatterDialogLoading(true);
    setFatal(null);
    try {
      const result = await api.clients();
      setClients(result.clients);
      setMatterDialogOpen(true);
    } catch (error) {
      setFatal(error instanceof Error ? error.message : "The matter form could not be opened.");
    } finally {
      setMatterDialogLoading(false);
    }
  };

  const matterCreated = (result: { client: Client; matter: Matter; channel: Channel }) => {
    setClients((current) => [result.client, ...current.filter((client) => client.id !== result.client.id)]);
    setMatters((current) => [result.matter, ...current.filter((matter) => matter.id !== result.matter.id)]);
    setMatterDialogOpen(false);
    setMatterId(result.matter.id);
    setChannels([result.channel]);
    setChannelId(result.channel.id);
  };

  const channelCreated = (channel: Channel) => {
    setChannels((current) => [...current.filter((item) => item.id !== channel.id), channel]);
    setChannelId(channel.id);
    setChannelDialogOpen(false);
    void refreshChannels().catch((error: Error) => setFatal(error.message));
  };

  const channelDeleted = (deletedChannelId: string) => {
    const remaining = channels.filter((channel) => channel.id !== deletedChannelId);
    setChannels(remaining);
    setChannelId((current) => current === deletedChannelId ? remaining[0]?.id ?? null : current);
    setChannelToDelete(null);
    void refreshChannels().catch((error: Error) => setFatal(error.message));
  };

  if (loading) return <div className="splash"><div className="brand-mark">P+</div><p>Preparing your matters…</p></div>;
  if (fatal && !lawyer) return <div className="auth-error"><div className="brand-mark">P+</div><h1>ParaPlus</h1><p>{fatal}</p><button onClick={() => window.location.reload()}>Try again</button></div>;
  if (!lawyer) return <Login onSignedIn={loadWorkspace} />;

  return (
    <div className="app-shell">
      <nav className="matter-rail" aria-label="Matters">
        <span className="rail-brand" title="ParaPlus">
          <img className="rail-brand-icon" src={paraPlusLogo} alt="ParaPlus" />
        </span>
        <div className="rail-items">
          {matters.map((matter) => (
            <button className={matter.id === matterId ? "active" : ""} key={matter.id} onClick={() => setMatterId(matter.id)} title={`${matter.matterNumber} — ${matter.name}`}>
              {initials(matter.name)}
              {matter.id === matterId && <i />}
            </button>
          ))}
          <button className="rail-add" onClick={() => void openMatterDialog()} title="Open a new matter" aria-label="Open a new matter">＋</button>
        </div>
        <div className="rail-utility-buttons">
          <button className="rail-firm-tools" onClick={() => setFirmToolsOpen((open) => !open)} title="Firm tools" aria-label="Open firm tools"><FirmToolsIcon /></button>
          <button className="rail-skill" onClick={() => setSkillsOpen(true)} title="Agent Skills" aria-label="Manage Agent Skills"><SkillsIcon /></button>
          <PwaControls />
        </div>
      </nav>

      <aside className="channel-sidebar">
        <header>
          <div className="sidebar-matter-summary">
            <span>MATTER</span>
            <h1>{selectedMatter?.name ?? "No matter selected"}</h1>
            {selectedMatter && (
              <>
                <p>{selectedMatter.matterNumber}</p>
                <div className="sidebar-matter-tags"><span>{selectedMatter.status}</span><span>{selectedMatter.type}</span></div>
              </>
            )}
          </div>
          <button className="icon-button" disabled={!selectedMatter} onClick={() => setSearchOpen((value) => !value)} aria-label="Search matter">⌕</button>
        </header>
        <div className="sidebar-heading">
          <span>Channels</span>
          <button
            onClick={() => selectedMatter ? setChannelDialogOpen(true) : void openMatterDialog()}
            title={selectedMatter ? "Create a channel" : "Open a matter first"}
            aria-label={selectedMatter ? "Create a channel" : "Open a matter"}
          >＋</button>
        </div>
        <div className="channel-list">
          {channels.map((channel) => (
            <button className={channel.id === channelId ? "active" : ""} key={channel.id} onClick={() => setChannelId(channel.id)}>
              <span className="hash">{channel.isPrivate ? "⌑" : "#"}</span><span>{channel.name}</span>
              {channel.unreadCount > 0 && <b>{channel.unreadCount}</b>}
            </button>
          ))}
        </div>
        <footer aria-label="Signed in user">
          <div className="sidebar-avatar">{initials(`${lawyer.firstName} ${lawyer.lastName}`)}</div>
          <div><strong>{`${lawyer.firstName} ${lawyer.lastName}`}</strong><span>{lawyer.role}</span></div>
          <button
            className="logout-button"
            type="button"
            title="Sign out"
            aria-label="Sign out"
            onClick={async () => {
              try {
                await api.logout();
                setLawyer(null);
                setMatters([]);
                setChannels([]);
                setMatterId(null);
                setChannelId(null);
              } catch (error) {
                setFatal(error instanceof Error ? error.message : "ParaPlus could not sign out.");
              }
            }}
          >
            ↪
          </button>
        </footer>
      </aside>

      {searchOpen && selectedMatter && (
        <div className="search-panel">
          <div className="search-panel-heading"><strong>Search {selectedMatter.matterNumber}</strong><button onClick={() => setSearchOpen(false)} aria-label="Close search">×</button></div>
          <input autoFocus value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search this matter" />
          {searchResults.map((message) => (
            <button key={message.messageId} onClick={() => { setChannelId(message.channelId); setSearchOpen(false); }}>
              <strong>#{message.channelName}</strong><span>{message.body}</span>
            </button>
          ))}
        </div>
      )}

      {lawyer && selectedMatter && selectedChannel ? (
        <ChannelWorkspace
          key={selectedChannel.id}
          currentLawyerId={lawyer.id}
          matter={selectedMatter}
          matters={matters}
          channel={selectedChannel}
          channels={channels}
          onSelectMatter={setMatterId}
          onSelectChannel={setChannelId}
          onChannelActivity={refreshChannels}
          onSearch={() => setSearchOpen((value) => !value)}
          onCreateMatter={() => void openMatterDialog()}
          onCreateChannel={() => setChannelDialogOpen(true)}
          onOpenTools={() => setFirmToolsOpen(true)}
          onOpenSkills={() => setSkillsOpen(true)}
          canDeleteChannel={lawyer.role === "Admin"}
          onDeleteChannel={() => setChannelToDelete(selectedChannel)}
        />
      ) : (
        <EmptyWorkspace
          hasMatters={matters.length > 0}
          openingMatter={matterDialogLoading}
          onCreateMatter={() => void openMatterDialog()}
        />
      )}
      {matterDialogOpen && (
        <NewMatterDialog clients={clients} onClose={() => setMatterDialogOpen(false)} onCreated={matterCreated} />
      )}
      {channelDialogOpen && selectedMatter && (
        <NewChannelDialog matter={selectedMatter} onClose={() => setChannelDialogOpen(false)} onCreated={channelCreated} />
      )}
      {channelToDelete && (
        <DeleteChannelDialog
          channel={channelToDelete}
          onClose={() => setChannelToDelete(null)}
          onDeleted={channelDeleted}
        />
      )}
      {skillsOpen && <AgentSkillsDialog onClose={() => setSkillsOpen(false)} />}
      {firmToolsOpen && lawyer && <FirmToolsMenu matter={selectedMatter} lawyer={lawyer} onClose={() => setFirmToolsOpen(false)} />}
      {fatal && lawyer && <div className="toast">{fatal}<button onClick={() => setFatal(null)}>×</button></div>}
    </div>
  );
}
